/**
 * Настройки · Ф2.3b + Ф-D — тема (каркас 2.2) · ВИД КАРТОЧКИ · ЗВУК.
 *
 * Звук (Ф-D): «Что озвучивать» (Выкл / Слово / Слово и фраза) · «Проговаривать новое слово
 * само» · «Скорость» (Стандартная / Медленная). Все три — пер-юзерные строки `settings`,
 * дефолт подставляет API. «Медленная» = плеер читает ту же запись на 0.7, второго набора
 * файлов нет. Выключил звук целиком — остальные две строки гаснут: настраивать нечего.
 * ⚠ Метки кнопок — `data-sndmode/sndauto/sndspeed`, НЕ `data-snd`: последним помечены
 * кнопки самого плеера (`src/audio.ts`), и пересечение стоило нам затёртого `aria-pressed`.
 *
 * ⭐⭐ Окно М1 (27.07): вид карточки больше НЕ выбирается человеком — задание выдаёт
 * лестница подачи по зрелости слова (`src/ladder.ts`, методика —
 * `knowledge/methodology/delivery-ladder.md`). Здесь остаётся тумблер
 * `delivery` (Лестница / Сам выбираю): ручной режим нужен, чтобы посмотреть любой вид
 * на любом слове, и по умолчанию выключен. Старое значение `card_mode` цело и действует
 * только в ручном режиме — миграции и записей в БД для этого не потребовалось.
 * ⛔ SRS от вида не зависит: все пять кладут в память ту же оценку 0/1/2.
 *
 * 🆕 Ф2.6 (30.07): карточка «Напоминания» — тумблер и время, обе строки пер-юзерные
 * (`push_enabled` / `push_time`, белый список `PATCH /settings`). ⛔ Витрина ничего не
 * шлёт и ничего не планирует: сообщение отправляет `core/push.py` по расписанию
 * планировщика Windows, а время считается по `settings.tz` ЧЕЛОВЕКА. Правила разбора
 * значений вынесены в `src/reminder.ts` — иначе их нечем проверить запуском.
 *
 * Остальные настройки (темп, наборы слов) приходят своими под-этапами — пока показаны
 * только для чтения.
 */

import { patchSettings } from "../api";
import { bindReport, esc, reportCardHtml } from "./feedback";
import { type AudioMode, type AudioSpeed, prefs as audioPrefs } from "../audio";
import { CHARACTERS, drawMascot } from "../mascot";
import { CONSENT_DETAIL, consentSummary } from "../leaderboard";
import { isOn as tradingIsOn, summary as tradingSummary,
         warning as tradingWarning } from "../trading";
import { ageTexts, needsAgeConfirm, packTexts, type PackState,
         summary as packSummary, visible as packVisible } from "../packs";
import {
  WORD_FALLBACK_TIME,
  isOn as pushIsOn,
  normalizeTime,
  summary as pushSummary,
  timeValue,
  wordSummary,
} from "../reminder";
import { confirmAdult, setAudioPrefs, setCharacterKey, setPackEnabled, setStatsOptIn,
         setThemePref, setTradingEnabled, state,
         xp as xpState } from "../state";
import { tap, type ThemePref } from "../tg";
import { go, render } from "../router";
import { mascotLevel } from "../xp";

const THEME_LABEL: [ThemePref, string][] = [
  ["light", "Свет"],
  ["dark", "Тьма"],
  ["auto", "Как в Telegram"],
];

/** Канон имён режимов — из плана Ф2 (правка при приёмке 25.07). Порядок: от тихого к активному. */
const MODES: { key: string; title: string; desc: string }[] = [
  { key: "silence", title: "Тишина", desc: "Только слово, ничего лишнего. Вспомнил — проверь себя." },
  { key: "dossier", title: "Досье", desc: "Слово, транскрипция, часть речи. Вспомнил — проверь себя." },
  { key: "choice", title: "Выбор", desc: "Угадай перевод из четырёх. Оценку выставим сами." },
  { key: "build", title: "Собери фразу",
    desc: "Выложи предложение кнопками-словами — без клавиатуры. Среди слов есть лишние, " +
          "порядок проверяется. Даётся и новым словам: слово с переводом стоит над заданием." },
  { key: "context", title: "Контекст",
    desc: "Впиши пропущенное слово в предложение — в нужной форме. Даётся только окрепшим " +
          "словам: новые сперва спросим выбором." },
];

/**
 * ⭐ М1: лестница подачи для человека. Номера — из методики
 * (`knowledge/methodology/delivery-ladder.md`), пропуски (3 и 5) там же объяснены:
 * эти ступени ещё не построены, их место пока занимает ступень ниже.
 * ⭐ М2 (27.07): добавилась ступень «1а» — на слух. Она даётся только при включённом
 * звуке; выключил — её место занимает ступень 1, и занятие идёт как прежде.
 * ⭐ М3 (28.07): построена ступень «3» — пропуск выбором; ввод руками (ступень 4) отодвинут
 * на бокс 4. Ступень «2» осталась запасной дорогой, пока её не построят окном М4.
 * ⭐ М6 (30.07): список стал ПОЛНЫМ — дописана ступень «5» (её построило окно М5, а строку
 * здесь тогда не добавили) и уточнена «6»: теперь у беглости есть окно в 5 секунд.
 */
const LADDER: { n: string; title: string; what: string }[] = [
  { n: "0", title: "Знакомство", what: "смотрим и слушаем новое слово" },
  { n: "1", title: "Узнаю перевод", what: "выбрать перевод из четырёх" },
  { n: "1а", title: "На слух", what: "слышим слово без текста — выбрать перевод" },
  { n: "2", title: "Достаю слово", what: "выбрать английское по переводу" },
  { n: "3", title: "Слово в предложении", what: "выбрать кнопкой, что стоит в пропуске" },
  { n: "4", title: "Вписываю сам", what: "впечатать слово в предложение" },
  { n: "5", title: "Фраза на слух", what: "слышим предложение целиком — вставить слово" },
  { n: "6", title: "Беглость", what: "своё слово — успеть за 5 секунд" },
];

/** Озвучка (Ф-D): что звучит · проговаривать ли само · скорость. Дефолты — от API. */
const SOUND: [AudioMode, string][] = [
  ["off", "Выкл"], ["word", "Слово"], ["word_phrase", "Слово и фраза"],
];
const SPEED: [AudioSpeed, string][] = [["normal", "Стандартная"], ["slow", "Медленная"]];

// ⭐ Какой набор ждёт подтверждения возраста прямо сейчас. Живёт НА ЭКРАНЕ: ответ
// человека хранит ядро (`settings.adult_confirmed`), а это лишь «карточка открыта».
let askAdultFor: string | null = null;

/**
 * Показать спрос про возраст и ПОДВЕСТИ ЕГО ПОД ГЛАЗА.
 *
 * ⚠ Дефект живой обкатки 21.08: перерисовка экрана роняет прокрутку контейнера в начало,
 * а карточка спроса стоит внизу — человек видел только «меня выкинуло наверх» и не
 * понимал, что надо подтвердить возраст. Молчаливый отказ хуже отказа с объяснением.
 */
function showAsk(key: string): void {
  askAdultFor = key;
  render("settings");
  const card = document.getElementById("adultAsk");
  if (!card) return;
  // ⚠ Прокручиваем СРАЗУ и САМИ, ⛔ не через requestAnimationFrame и не «плавно»:
  // в свёрнутой вкладке кадры не идут вовсе, и подвод под глаза молча не срабатывал
  // (поймано замером: карточка спроса стояла на 2690-м пикселе при окне 812).
  const box = document.getElementById("scroll");
  if (!box) { card.scrollIntoView({ block: "center" }); return; }
  const shift = card.getBoundingClientRect().top - box.getBoundingClientRect().top;
  const center = Math.max(0, (box.clientHeight - card.offsetHeight) / 2);
  box.scrollTop = Math.max(0, box.scrollTop + shift - center);
}

/** Карточка одного тематического набора: тумблер, подпись, пометка 18+. */
function packCard(pk: PackState): string {
  const t = packTexts(pk);
  const on = pk.enabled;
  return '<div class="card">' +
    `<p class="h">${esc(t.title)}` +
    (t.badge ? ` <span class="badge18">${esc(t.badge)}</span>` : "") + "</p>" +
    `<p class="hsub">${esc(t.body)}</p>` +
    '<div class="set-row wide"><div class="lbl">' +
      `<div class="t">Учить${t.badge ? " (18+)" : ""}</div>` +
      '<div class="s">приходит вперемешку с обычными словами</div></div>' +
      segment(`packSwitch_${pk.key}`, t.title, [["1", t.on], ["0", t.off]],
              on ? "1" : "0", "pack", { two: true }) +
    "</div>" +
    `<p class="footnote">${esc(packSummary(pk))}</p>` +
    // ⚠ 21.08, по живой обкатке владельца: раньше нажатие «Вкл» без подтверждения
    // просто перерисовывало экран — прокрутка уезжала наверх, и человек не понимал,
    // что от него хотят. Теперь у самого тумблера стоит объяснение, а карточка спроса
    // подводится под глаза (см. `showAsk` ниже).
    (askAdultFor === pk.key
      ? '<p class="footnote warn">Чтобы включить, подтвердите возраст — вопрос ниже.</p>'
      : "") +
    (askAdultFor === pk.key ? adultCard(pk) : "") +
    "</div>";
}

/** ⭐⭐ Спрос про возраст. ⛔ Это вежливость экрана — правило всё равно в ядре. */
function adultCard(pk: PackState): string {
  const a = ageTexts();
  return '<div class="card ask" id="adultAsk">' +
    `<p class="h">${esc(a.title)}</p>` +
    `<p class="hsub">${esc(a.body)}</p>` +
    '<div class="askrow">' +
      `<button type="button" class="askbtn yes" data-adult="1" data-pack="${pk.key}">${esc(a.ask)}</button>` +
      `<button type="button" class="askbtn no" data-adult="0">${esc(a.no)}</button>` +
    "</div>" +
    `<p class="footnote">${esc(a.foot)}</p>` +
  "</div>";
}

/** ⚠ Почему подсветка по умолчанию выключена — сказано человеку прямо, а не спрятано. */
const READER_HL_DETAIL =
  "«Всё» подчёркивает почти каждое слово — страница перестаёт быть страницей";

function segment(id: string, label: string, items: [string, string][], now: string,
                 attr: string, o: { off?: boolean; two?: boolean } = {}): string {
  const cls = `switch2${o.two ? " two" : ""}${o.off ? " off" : ""}`;
  return `<div class="${cls}" id="${id}" role="group" aria-label="${label}">` +
    items.map(([v, text]) =>
      `<button type="button" data-${attr}="${v}" aria-pressed="${v === now}"` +
      `${o.off ? " disabled" : ""}>${text}</button>`).join("") +
    "</div>";
}

async function setMode(key: string): Promise<void> {
  if (state.me) state.me.settings.card_mode = key;      // на экране — сразу
  render("settings");
  try {
    await patchSettings({ card_mode: key });
  } catch {
    /* не сохранилось — вид карточки вернётся к прежнему при следующей загрузке */
  }
}

/** ⭐ М1: лестница или ручной выбор вида. Дефолт — лестница, ручной режим = отладка. */
async function setDelivery(key: string): Promise<void> {
  if (state.me) state.me.settings.delivery = key;
  render("settings");
  try {
    await patchSettings({ delivery: key });
  } catch {
    /* не сохранилось — вернётся к прежнему при следующей загрузке */
  }
}

/**
 * Ф2.6: напоминание. Витрина только ХРАНИТ выбор — шлёт `core/push.py` по расписанию
 * планировщика. Поэтому здесь ни таймеров, ни уведомлений браузера: закрытая вкладка
 * ничего не должна ломать.
 */
async function setPush(changes: Record<string, string | number>): Promise<void> {
  if (state.me) Object.assign(state.me.settings, changes);
  render("settings");
  try {
    await patchSettings(changes);
  } catch {
    /* не сохранилось — вернётся к прежнему при следующей загрузке */
  }
}

export function renderSettings(host: HTMLElement): void {
  const s = state.me?.settings;
  const pref = state.themePref;
  const raw = s?.card_mode ?? "dossier";
  // Все четыре вида живые (Ф2.3b) — отмечаем ровно то, что выбрано. Незнакомое
  // значение в базе (ручная правка) не должно оставлять экран без отметки.
  const mode = MODES.some((m) => m.key === raw) ? raw : "dossier";
  // ⭐ М1: по умолчанию задание выбирает лестница; ручной список видов — режим отладки.
  const delivery = String(s?.delivery ?? "ladder") === "manual" ? "manual" : "ladder";
  const snd = audioPrefs();
  const off = snd.mode === "off";      // звук выключен целиком — настраивать нечего
  // ⭐ Ф2.5: герой и его уровень — из ядра (`/me` → `xp`), витрина их не считает.
  const x = xpState();
  const charKey = x.character;
  const level = mascotLevel(x);
  const charName = CHARACTERS.find((c) => c.key === charKey)?.name ?? CHARACTERS[0].name;
  // 🆕 Ф2.6: напоминание. Значения приходят из ядра (`db.DEFAULT_SETTINGS`), витрина
  // своего дефолта не придумывает — только приводит к виду, который понимает поле ввода.
  const pushOn = pushIsOn(s?.push_enabled);
  const tr = (state.me as any)?.trading;
  const tradingOn = tradingIsOn(tr?.enabled);
  // ⚠ Та же грабля, что у напоминаний: значение приезжает то числом, то строкой —
  //    поэтому читаем тем же разборщиком, а не сравнением с единицей.
  const statsOn = pushIsOn(s?.stats_opt_in);
  // Фаза 3 «книги»: вид книжной страницы (дефолты живут в ядре, не здесь).
  const readSize = String(s?.reader_size ?? "m");
  const readHl = String(s?.reader_highlight ?? "off");
  const pushTime = timeValue(s?.push_time);
  // 🆕 Окно 2 запуска: слово дня. Своё время и свой тумблер — но читаются тем же
  // разборщиком (значение приезжает то числом, то строкой).
  const wordOn = pushIsOn(s?.word_enabled);
  const wordTime = timeValue(s?.word_time, WORD_FALLBACK_TIME);

  host.innerHTML =
    '<div class="two-col"><div class="col-main">' +
      '<div class="greet"><h2>Настройки</h2></div>' +
      '<div class="card">' +
        '<p class="h">Вид</p>' +
        '<p class="hsub">Тема запоминается за тобой: у каждого в семье своя.</p>' +
        '<div class="set-row wide"><div class="lbl"><div class="t">Тема</div>' +
          '<div class="s">«Как в Telegram» подхватывает тему клиента на ходу</div></div>' +
          '<div class="switch2" id="themeSwitch" role="group" aria-label="Тема">' +
            THEME_LABEL.map(([v, label]) =>
              `<button type="button" data-theme="${v}" aria-pressed="${v === pref}">${label}</button>`).join("") +
          "</div>" +
        "</div>" +
      "</div>" +
      '<div class="card">' +
        '<p class="h">Задания</p>' +
        '<p class="hsub">По умолчанию задание выбирается САМО — по тому, насколько слово ' +
        "окрепло: сперва знакомство, потом узнавание, дальше «впиши сам». Это лестница " +
        "подачи, и менять её вручную не нужно.</p>" +
        '<div class="set-row wide"><div class="lbl"><div class="t">Как выдавать карточки</div>' +
          '<div class="s">«Сам выбираю» — для проверки видов, слово получит его независимо от зрелости</div></div>' +
          segment("deliverySwitch", "Как выдавать карточки",
                  [["ladder", "Лестница"], ["manual", "Сам выбираю"]], delivery,
                  "delivery", { two: true }) +
        "</div>" +
        (delivery === "ladder"
          ? '<div class="ladderline">' +
              LADDER.map((r) =>
                `<div class="lrow"><span class="ln">${r.n}</span>` +
                `<span class="lt">${r.title}</span><span class="lw">${r.what}</span></div>`).join("") +
            "</div>" +
            '<p class="footnote" style="margin-top:12px">Ступени «пропуск выбором» и ' +
            "«фраза на слух» появятся следующими шагами — пока их место занимает ближайшая " +
            "ступень ниже. Ступень «на слух» работает, только если звук включён.</p>"
          : '<div class="modepick" id="modePick">' +
              MODES.map((m) =>
                `<button class="modeitem" type="button" data-mode="${m.key}" ` +
                `aria-pressed="${m.key === mode}">` +
                `<div><div class="mt">${m.title}</div><div class="md">${m.desc}</div></div>` +
                '<span class="tick">✓</span>' +
                "</button>").join("") +
            "</div>" +
            '<p class="footnote" style="margin-top:12px">В «Контексте» форма важна: если ' +
            "предложение требует <b>has gone</b>, ответ «go» не примем — это и есть тренировка " +
            "грамматики. У слова, к которому нет предложения с пропуском, карточка покажется как «Досье».</p>") +
      "</div>" +
      '<div class="card">' +
        '<p class="h">Звук</p>' +
        '<p class="hsub">Слово читает женский голос, фразу-пример — мужской, оба ' +
        'американские. ⭐ Звук доступен только тогда, когда английское слово уже на ' +
        'экране: пока оно скрыто, кнопка выдала бы ответ.</p>' +
        '<div class="set-row wide"><div class="lbl"><div class="t">Что озвучивать</div>' +
          '<div class="s">фраза целиком звучит в «Контексте» — после сверки</div></div>' +
          segment("sndMode", "Звук", SOUND as [string, string][], snd.mode, "sndmode") +
        "</div>" +
        '<div class="set-row wide"><div class="lbl">' +
          '<div class="t">Проговаривать новое слово само</div>' +
          '<div class="s">без наушников выключается одним касанием</div></div>' +
          segment("sndAuto", "Проговаривать само", [["1", "Вкл"], ["0", "Выкл"]],
                  snd.autoplay ? "1" : "0", "sndauto", { off, two: true }) +
        "</div>" +
        '<div class="set-row wide"><div class="lbl"><div class="t">Скорость</div>' +
          '<div class="s">«Медленная» — та же запись, читается медленнее; высота голоса не меняется</div></div>' +
          segment("sndSpeed", "Скорость", SPEED as [string, string][], snd.speed, "sndspeed",
                  { off, two: true }) +
        "</div>" +
        (off ? '<p class="footnote">Звук выключен — ни одного автозапуска.</p>' : "") +
      "</div>" +
      // ⭐ Ф2.5: выбор героя. Пишется в `user_progress.character` (слой 3), а НЕ в
      // настройки — второго источника правды о персонаже в приложении нет.
      // Каждый нарисован на ТЕКУЩЕМ уровне человека: видно, что растёт именно он.
      '<div class="card">' +
        '<p class="h">Персонаж</p>' +
        `<p class="hsub">С тобой растёт один герой — сейчас ${charName}, ` +
        `уровень ${level} из ${xpState().max_level || 12}. Сменить можно когда угодно: ` +
        "опыт и уровень остаются твоими, меняется только тот, кто их носит.</p>" +
        '<div class="charpick" id="charPick" role="group" aria-label="Персонаж">' +
          CHARACTERS.map((c) =>
            `<button class="charitem" type="button" data-char="${c.key}" ` +
            `aria-pressed="${c.key === charKey}">` +
            `<span class="cm">${drawMascot(level, false, c.key)}</span>` +
            `<span class="cn">${c.name}</span><span class="ch">${c.hint}</span>` +
            "</button>").join("") +
        "</div>" +
      "</div>" +
      // 🆕 Окно 3 (20.08): ТРЕЙДИНГ — доменный набор, который человек включает сам.
      // ⭐ Порог базы здесь не запрет, а предупреждение с ЕГО цифрой (решение
      // владельца «предупреждать, но пускать»). ⛔ Ни одного своего числа: сколько
      // слов, сколько до базы и какая доза — приезжает из ядра в /me.trading.
      (tr?.exists ? (
      '<div class="card">' +
        '<p class="h">Английский трейдинга</p>' +
        '<p class="hsub">Слова и фразы, которые встречаются в терминале, в правилах ' +
        "проп-фирм, в разборе сделок и в переписке с поддержкой.</p>" +
        '<div class="set-row wide"><div class="lbl"><div class="t">Учить трейдинг</div>' +
          '<div class="s">приходит вперемешку с обычными словами</div></div>' +
          segment("tradingSwitch", "Трейдинг", [["1", "Вкл"], ["0", "Выкл"]],
                  tradingOn ? "1" : "0", "trading", { two: true }) +
        "</div>" +
        `<p class="footnote">${tradingSummary(tr)}</p>` +
        `<p class="footnote warn">${tradingWarning(tr)}</p>` +
      "</div>") : "") +
      // 🆕 21.08: остальные тематические наборы — по карточке на каждый, ровно как
      // трейдинг. ⛔ Ни одной своей цифры: объём и доза приезжают в /me.packs.
      (((state.me as any)?.packs?.list || []) as PackState[])
        .filter(packVisible).map(packCard).join("") +
      // 🆕 Ф2.6: напоминание. Тумблер и время — пер-юзерные строки `settings`
      // (белый список `PATCH /settings`), схема БД не тронута. Шлёт бот по расписанию
      // ядра, поэтому подпись говорит ровно то, что ядро и делает.
      '<div class="card">' +
        '<p class="h">Напоминания</p>' +
        '<p class="hsub">Бот раз в день пишет в Telegram, сколько слов ждёт. ' +
        "Время у каждого в семье своё — по его часовому поясу.</p>" +
        '<div class="set-row wide"><div class="lbl"><div class="t">Напоминать в Telegram</div>' +
          '<div class="s">сообщение приходит только если есть что учить</div></div>' +
          segment("pushSwitch", "Напоминания", [["1", "Вкл"], ["0", "Выкл"]],
                  pushOn ? "1" : "0", "push", { two: true }) +
        "</div>" +
        '<div class="set-row"><div class="lbl"><div class="t">Во сколько</div>' +
          `<div class="s">по твоему поясу${s?.tz ? ` (${s.tz})` : ""}</div></div>` +
          `<input class="timein" id="pushTime" type="time" value="${pushTime}" ` +
          `aria-label="Время напоминания"${pushOn ? "" : " disabled"}>` +
        "</div>" +
        `<p class="footnote">${pushSummary({ enabled: pushOn, time: pushTime, tz: s?.tz })}</p>` +
      "</div>" +
      // 🆕 Окно 2 запуска: СЛОВО ДНЯ — второе касание за день, утром. Карточка стоит
      // рядом с напоминанием нарочно: это одна тема («что бот присылает»), и человек
      // должен видеть оба тумблера сразу — иначе два сообщения в день выглядят
      // неотключаемыми. ⛔ Витрина ничего не шлёт: отправкой владеет core/word_of_day.py.
      '<div class="card">' +
        '<p class="h">Слово дня</p>' +
        '<p class="hsub">Утром бот присылает одно слово со звуком и примером — то, ' +
        "которое ты ещё не начинал. Это знакомство вперёд: через день-два оно " +
        "придёт на занятие.</p>" +
        '<div class="set-row wide"><div class="lbl"><div class="t">Присылать слово дня</div>' +
          '<div class="s">приходит, даже если ты уже занимался сегодня</div></div>' +
          segment("wordSwitch", "Слово дня", [["1", "Вкл"], ["0", "Выкл"]],
                  wordOn ? "1" : "0", "word", { two: true }) +
        "</div>" +
        '<div class="set-row"><div class="lbl"><div class="t">Во сколько</div>' +
          `<div class="s">по твоему поясу${s?.tz ? ` (${s.tz})` : ""}</div></div>` +
          `<input class="timein" id="wordTime" type="time" value="${wordTime}" ` +
          `aria-label="Время слова дня"${wordOn ? "" : " disabled"}>` +
        "</div>" +
        `<p class="footnote">${wordSummary({ enabled: wordOn, time: wordTime, tz: s?.tz })}</p>` +
      "</div>" +
      // 🆕 Фаза 3 «книги»: вид книжной страницы. ⛔ Никаких следствий в данных —
      // это чистый вид, и меняет его сам человек. Подсветка ВЫКЛЮЧЕНА по умолчанию:
      // при «подсветить всё» подчёркнуто ~89 % слов, и страница перестаёт быть страницей.
      '<div class="card">' +
        '<p class="h">Чтение книг</p>' +
        '<p class="hsub">Кегль страницы можно менять и прямо в книге — кнопкой «Aa».</p>' +
        '<div class="set-row wide"><div class="lbl"><div class="t">Размер шрифта</div>' +
          '<div class="s">крупнее — меньше слов на странице</div></div>' +
          segment("readSize", "Размер шрифта",
                  [["s", "S"], ["m", "M"], ["l", "L"]], readSize, "rsize") +
        "</div>" +
        '<div class="set-row wide"><div class="lbl"><div class="t">Подчёркивать слова</div>' +
          '<div class="s">' + READER_HL_DETAIL + "</div></div>" +
          segment("readHl", "Подсветка",
                  [["off", "Выкл"], ["learn", "Что учу"], ["all", "Всё"]], readHl, "rhl") +
        "</div>" +
      "</div>" +
      // 🆕 Окно 4: имя в общей таблице. ⭐ Согласие про ЧУЖИЕ глаза: свои цифры человек
      // видит всегда, и в счётчике «занимаются N» он участвует в любом случае.
      '<div class="card">' +
        '<p class="h">Имя в общей таблице</p>' +
        '<p class="hsub">Экран «Вместе» показывает, кто сколько занимался. ' +
        "Имя видно только с твоего согласия — цифры считаются в любом случае.</p>" +
        '<div class="set-row wide"><div class="lbl"><div class="t">Показывать имя другим</div>' +
          `<div class="s">${CONSENT_DETAIL}</div></div>` +
          segment("statsSwitch", "Имя в таблице", [["1", "Вкл"], ["0", "Выкл"]],
                  statsOn ? "1" : "0", "stats", { two: true }) +
        "</div>" +
        `<p class="footnote">${consentSummary(statsOn)}</p>` +
      "</div>" +
      (s
        ? '<div class="card">' +
            '<p class="h">Занятие</p>' +
            '<p class="hsub">Пока только показываем — менять будем на следующих шагах.</p>' +
            '<div class="set-row"><div class="lbl"><div class="t">Новые слова в день</div>' +
              '<div class="s">повторять можно сколько угодно, новые ограничены нарочно</div></div>' +
              `<div class="val">${s.daily_new_words}</div></div>` +
            '<div class="set-row"><div class="lbl"><div class="t">Карточек в подходе</div>' +
              '<div class="s">сколько слов берём за один заход</div></div>' +
              `<div class="val">${s.session_size}</div></div>` +
            '<div class="set-row"><div class="lbl"><div class="t">Фразовых глаголов за подход</div>' +
              '<div class="s">они самые трудные: многословные, форма в предложении своя — ' +
              "поэтому идут дозой, а не сплошняком</div></div>" +
              `<div class="val">${s.phrasal_per_session ?? 2}</div></div>` +
            '<div class="set-row"><div class="lbl"><div class="t">Часовой пояс</div>' +
              '<div class="s">по нему считаются «сегодня» и серия дней</div></div>' +
              `<div class="val">${s.tz}</div></div>` +
          "</div>"
        : "") +
      // 🆕 Окно 5: заявка «не работает». Человек пишет строку — контекст приложение
      // собирает само (`src/feedback.ts`) и показывает, что именно приложит.
      reportCardHtml() +
      // 🆕 Окно 5: вход в админку. ⭐ Флаг `admin` приходит от ЯДРА; спрятанная кнопка
      // защитой не является — постороннему сам адрес отвечает 403.
      (state.me?.admin
        ? '<div class="card"><p class="h">Админ</p>' +
          '<p class="hsub">Люди, занятия, оценки и заявки — сводка в приложении, ' +
          "без запросов к серверу руками.</p>" +
          '<div class="askrow"><button type="button" class="askbtn yes" id="goAdmin">' +
          "Открыть сводку</button></div></div>"
        : "") +
      // 🆕 Окно 6: вход в «Опасную зону». ⭐ В САМОМ НИЗУ настроек и отдельным экраном —
      // это первый слой защиты; сам сброс требует вписать слово, и проверяет его ядро.
      '<div class="card"><p class="h">Начать с нуля</p>' +
      '<p class="hsub">Сбросить память слов, награды или опыт — по отдельности. ' +
      "Словарь и озвучка остаются целиком, и это касается только тебя.</p>" +
      '<div class="askrow"><button type="button" class="askbtn ghost" id="goDanger">' +
      "Опасная зона →</button></div></div>" +
      '<p class="footnote">Мои наборы слов и добавление своих слов ' +
      "появятся здесь дальше — по одному шагу за раз.</p>" +
    "</div></div>";

  bindReport(host, "settings");
  host.querySelector("#goAdmin")?.addEventListener("click", (e) => {
    tap(e.target as HTMLElement);
    go("admin");
  });
  host.querySelector("#goDanger")?.addEventListener("click", (e) => {
    tap(e.target as HTMLElement);
    go("danger");
  });

  host.querySelector("#themeSwitch")?.addEventListener("click", (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>("button[data-theme]");
    if (!btn) return;
    tap(btn);
    void setThemePref(btn.dataset.theme as ThemePref).then(() => render("settings"));
    render("settings");
  });

  /* Звук: настройка слышна сразу (скорость — даже на записи, которая уже играет),
     потом тихо уезжает в пер-юзерные `settings`. */
  const sound = (sel: string, attr: string, take: (v: string) => Parameters<typeof setAudioPrefs>[0]) => {
    host.querySelector(sel)?.addEventListener("click", (e) => {
      const btn = (e.target as HTMLElement).closest<HTMLButtonElement>(`button[data-${attr}]`);
      if (!btn || btn.disabled) return;
      tap(btn);
      void setAudioPrefs(take(btn.dataset[attr] as string)).then(() => render("settings"));
      render("settings");
    });
  };
  sound("#sndMode", "sndmode", (v) => ({ mode: v as AudioMode }));
  sound("#sndAuto", "sndauto", (v) => ({ autoplay: v === "1" }));
  sound("#sndSpeed", "sndspeed", (v) => ({ speed: v as AudioSpeed }));

  host.querySelector("#deliverySwitch")?.addEventListener("click", (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>("button[data-delivery]");
    if (!btn || (btn as HTMLButtonElement).disabled) return;
    tap(btn);
    void setDelivery(btn.dataset.delivery as string);
  });

  /* Персонаж: выбор виден сразу, потом тихо уезжает в слой 3 (`user_progress`). */
  host.querySelector("#charPick")?.addEventListener("click", (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>("button[data-char]");
    if (!btn) return;
    tap(btn);
    void setCharacterKey(btn.dataset.char as string).then(() => render("settings"));
    render("settings");
  });

  /* Ф2.6: тумблер напоминания и время. Время шлём, только если оно разобралось —
     мусор в API отправлять незачем, там его ждёт 400 (`api._valid_time`). */
  // Фаза 3 «книги»: кегль и подсветка. ⛔ Ни расчётов, ни следствий — только вид.
  for (const [sel, attr, key] of [["#readSize", "rsize", "reader_size"],
                                  ["#readHl", "rhl", "reader_highlight"]] as const) {
    host.querySelector(sel)?.addEventListener("click", (e) => {
      const b = (e.target as HTMLElement).closest<HTMLElement>(`button[data-${attr}]`);
      if (!b) return;
      tap(b);
      const value = b.dataset[attr === "rsize" ? "rsize" : "rhl"] as string;
      if (state.me) state.me.settings[key] = value;
      render("settings");
      void patchSettings({ [key]: value }).catch(() => undefined);
    });
  }

  host.querySelector("#statsSwitch")?.addEventListener("click", (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>("button[data-stats]");
    if (!btn) return;
    tap(btn);
    const on = btn.dataset.stats === "1";
    void setStatsOptIn(on).then(() => render("settings"));
    render("settings");                    // на экране — сразу, не дожидаясь сети
  });

  // 🆕 Окно 3: тумблер трейдинга. ⛔ Витрина ничего не решает — шлёт выбор в ядро
  // и перерисовывает экран его ответом (иначе подпись «в наборе N слов» отстаёт).
  host.querySelector("#tradingSwitch")?.addEventListener("click", (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>("button[data-trading]");
    if (!btn) return;
    tap(btn);
    void setTradingEnabled(btn.dataset.trading === "1")
      .finally(() => go("settings"));
  });

  // 🆕 21.08: тумблеры наборов. ⚠ У взрослого набора сперва спрашиваем возраст —
  // получить 400 в лицо плохой способ узнать о правиле; ⛔ но правило живёт в ядре.
  host.addEventListener("click", (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>("button[data-pack]");
    if (!btn) return;
    const box = btn.closest<HTMLElement>("[id^='packSwitch_']");
    if (!box) return;
    const key = box.id.replace("packSwitch_", "");
    const list: PackState[] = ((state.me as any)?.packs?.list || []);
    const pk = list.find((x) => x.key === key);
    if (!pk) return;
    tap(btn);
    const on = btn.dataset.pack === "1";
    const confirmed = Boolean((state.me as any)?.packs?.adult_confirmed);
    if (needsAgeConfirm(pk, confirmed, on)) {
      showAsk(key);                      // спрашиваем ДО включения — и показываем спрос
      return;
    }
    askAdultFor = null;
    // ⭐ Если ядро всё-таки отказало (например, подтверждение сняли в другом окне) —
    // не роняем человека молча, а показываем тот же спрос. ⛔ Правило живёт в ядре,
    // экран только объясняет отказ.
    void setPackEnabled(key, pk.setting, on)
      .then(() => go("settings"))
      .catch(() => { if (pk.adult && on) showAsk(key); else go("settings"); });
  });

  host.querySelector("#adultAsk")?.addEventListener("click", (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>("button[data-adult]");
    if (!btn) return;
    tap(btn);
    const key = btn.dataset.pack || "";
    askAdultFor = null;
    if (btn.dataset.adult !== "1") { render("settings"); return; }
    const pk = (((state.me as any)?.packs?.list || []) as PackState[]).find((x) => x.key === key);
    void confirmAdult()
      .then(() => (pk ? setPackEnabled(key, pk.setting, true) : undefined))
      .finally(() => go("settings"));
  });

  host.querySelector("#pushSwitch")?.addEventListener("click", (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>("button[data-push]");
    if (!btn) return;
    tap(btn);
    void setPush({ push_enabled: btn.dataset.push === "1" ? 1 : 0 });
  });
  host.querySelector("#pushTime")?.addEventListener("change", (e) => {
    const field = e.target as HTMLInputElement;
    const value = normalizeTime(field.value);
    if (!value) {
      field.value = pushTime;              // не разобралось — возвращаем прежнее
      return;
    }
    void setPush({ push_time: value });
  });

  // 🆕 Окно 2 запуска: слово дня — тот же путь, что у напоминания (тот же белый список).
  host.querySelector("#wordSwitch")?.addEventListener("click", (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>("button[data-word]");
    if (!btn) return;
    tap(btn);
    void setPush({ word_enabled: btn.dataset.word === "1" ? 1 : 0 });
  });
  host.querySelector("#wordTime")?.addEventListener("change", (e) => {
    const field = e.target as HTMLInputElement;
    const value = normalizeTime(field.value);
    if (!value) {
      field.value = wordTime;              // не разобралось — возвращаем прежнее
      return;
    }
    void setPush({ word_time: value });
  });

  host.querySelector("#modePick")?.addEventListener("click", (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>("button[data-mode]");
    if (!btn || (btn as HTMLButtonElement).disabled) return;
    tap(btn);
    void setMode(btn.dataset.mode as string);
  });
}
